// --- React & Redux
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
// --- Hooks
import { useDialogsActions } from '../hooks';
import { useDialogsForm } from '../forms';
import { useLoading } from '../../../utils/hooks/useLoading';
// --- API
import { datastageApi } from '../../../api';
// --- Actions
import { useProductsActions } from '../../../store/products/useProducts';
// --- UI Components
import Typography from '@dtci/spellbook/Typography';
import { Col, Row } from 'antd';
import Button from '@dtci/spellbook/Button';
import Input from '@dtci/spellbook/Input';
import { InputLabel } from '../../InputLabel/InputLabel';
// --- Utilities
import moment from 'moment';
import { filterCriteria } from '../../../utils/filterCriteria';
import { useToastNotification } from '../../ToastNotifications';
import {saveAs} from 'file-saver';

/**
 * VersionForm Component
 *
 * This component renders a form for managing versions of a product.
 * It includes functionality to add a new version and save it.
 * It also displays existing versions of the product.
 *
 * @component
 * @param {Object} data - The data object containing product information.
 * * @returns {JSX.Element} The rendered VersionForm component.
 */
const VersionForm = ({ data }) => {
    const form = useDialogsForm();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { versions, filterForm, productRange, productSort } = useSelector((s) => s.products);
    const { updateProducts } = useProductsActions();
    const { openToast } = useToastNotification();
    const [addNewVersion, setAddNewVersion] = useState(false);
    const [savedVersions, setSavedVersions] = useState(versions);

    // Function to handle the change event of the input field
    const handleOnchange = (e) => {
        form.setValue('title', e.target.value);
    };

    // Function to handle the change event of the select input
    const handleAddVersion = () => {
        setAddNewVersion(true);
    };

    // Function to handle the save version event
    const handleSaveVersion = async () => {
        setLoadingTrue();
        try {
            // Save the new version using the API
            const res = await datastageApi.saveVersion({
                productId: data.productId,
                title: form.state.formData?.title,
            });

            if (res?.status === 200) {
                // Fetch the updated versions from the API
                const ver = await datastageApi.versions(data.productId);

                // Update the versions state with the new data
                setSavedVersions(ver);
                openToast('Version Created Successfully', 'success');
            } else {
                openToast(
                    `${(res?.statusText && res?.statusText) || 'Version Creation Failed'}`,
                    'error',
                );
            }
        } catch (error) {
            console.error('Error saving version:', error);
            openToast('Version Creation Failed', 'error');
        } finally {
            setAddNewVersion(false);
            form.reset();
            setLoadingFalse();
        }
    };

    // Function to handle the cancel event
    const handleCancel = () => {
        setAddNewVersion(false);
        form.reset();
    };

    const downloadAttachment = async (rightsLineId, fileName) => {
        setLoadingTrue();
      const res = await datastageApi.downloadAttachment(rightsLineId, fileName);
        saveAs(res.data, fileName);
        setLoadingFalse();
    };

    return (
        <div className="flexBox">
            {/* Display title and associated product ID */}
            <Typography variant="T20" bold>
                {data.title} ({data.productId})
            </Typography>

            {/* Check if there are saved versions to display */}
            {savedVersions?.length > 0 ? (
                savedVersions?.map((ver) => (
                    <>
                        {/* Link to view/download a specific version */}
                        <Button
                            key={ver?.rightslineId}
                            type="button"
                            styleType="link"
                            onClick={() => downloadAttachment(ver?.rightslineId, `${ver?.title}.pdf`)}
                        >
                            Version: {ver.version} | {ver.title} | {ver?.createdBy?.fullName} -{' '}
                            {moment(ver.createdAt).format('DD/MMM/YYYY')}
                        </Button>
                        <br />
                        <br />
                    </>
                ))
            ) : (
                /* Message when no versions exist */
                <Typography key={window.crypto.randomUUID()} variant="T20">
                    No Versions Created Yet
                </Typography>
            )}

            {/* Check if user is adding a new version */}
            {addNewVersion ? (
                <>
                    {/* Input field for new version name */}
                    <Row>
                        <InputLabel colProps={{ span: 15 }} label="Version Name">
                            <Input
                                value={form.state.formData?.title ?? ''}
                                onChange={(e) => handleOnchange(e)}
                                width="auto"
                                id="title"
                                name="title"
                                placeholder="Enter"
                            />
                        </InputLabel>
                    </Row>

                    {/* Row for action buttons */}
                    <Row>
                        <Col span={8} />
                        {/* Cancel button */}
                        <Button
                            iconType="fa-regular fa-ban"
                            type="button"
                            styleType="link"
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>

                        {/* Save version button */}
                        <Button
                            iconType="fa-regular fa-floppy-disk"
                            type="button"
                            styleType="link"
                            onClick={handleSaveVersion}
                        >
                            Save Version
                        </Button>
                    </Row>
                </>
            ) : (
                /* Button to trigger creation of a new version */
                <Button
                    iconType="fa-regular fa-plus"
                    type="button"
                    styleType="link"
                    onClick={handleAddVersion}
                >
                    Create New Version
                </Button>
            )}
        </div>
    );
};

/**
 * useVersionDialog Hook
 *
 * This hook provides functionality to open a version selection dialog.
 * It uses the useDialogsActions hook to manage the dialog state.
 * It also provides an openVersionDialog function to open the dialog.
 *
 * @returns {Object} An object containing the openVersionDialog function.
 */
export const useVersionDialog = () => {
    const { openDialog } = useDialogsActions();

    // Function to open the version dialog
    const openVersionDialog = (data, onConfirm) =>
        openDialog({
            title: `Clearance Memo Versions`,
            content: <VersionForm data={data} />,
            closeAction: true,
        });

    return { openVersionDialog };
};
